import { useTranslation } from "react-i18next";
import { z } from "zod";

const useAddCardSchema = () => {
  const { t } = useTranslation(["checkout"]);

  return z.object({
    first_name: z
      .string()
      .min(1, t("checkout:add-card-first-name-required", "First name is required")),
    last_name: z
      .string()
      .min(1, t("checkout:add-card-last-name-required", "Last name is required")),
    // The gateway needs a contactable address for the card, and the client record often
    // has none: an invoice can be raised against a client nobody ever emailed. The payer
    // supplies it here instead of the request going out with an empty one and 422ing.
    email: z
      .string()
      .min(1, t("checkout:add-card-email-required", "Email is required"))
      .email(t("checkout:add-card-email-invalid", "Enter a valid email address")),
    cardholder_name: z
      .string()
      .min(1, t("checkout:add-card-cardholder-name-required", "Card holder name is required"))
      .max(50, t("checkout:add-card-cardholder-name-max", "Card holder name must be at most 50 characters"))
      .optional(),
    card_number: z.boolean().refine((v) => v, {
      message: t("checkout:add-card-card-number-required", "Card number is required"),
    }),
    card_expiry: z.boolean().refine((v) => v, {
      message: t("checkout:add-card-card-expiry-required", "Expiry date is required"),
    }),
    card_cvc: z.boolean().refine((v) => v, {
      message: t("checkout:add-card-card-cvc-required", "CVC is required"),
    }),
  });
};

export type AddCardFormValues = z.infer<ReturnType<typeof useAddCardSchema>>;

export default useAddCardSchema;
